import React from 'react';
import performanceManager from '../utils/performanceManager';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `error_${Date.now()}_${Math.random()}`;
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // Track error with performance manager
    performanceManager.trackError(errorId, error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.componentName || 'ErrorBoundary',
      props: this.props,
      retryCount: this.state.retryCount
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, you might want to log to an error reporting service
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryCount: prevState.retryCount + 1
    }));

    // Clear the error from performance manager
    if (this.state.errorId) {
      performanceManager.clearError(this.state.errorId);
    }

    // Call custom retry handler if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI from props
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      const isNetworkError = this.state.error?.message?.includes('fetch') || 
                            this.state.error?.message?.includes('network') ||
                            this.state.error?.message?.includes('Failed to load');

      const isMemoryError = this.state.error?.message?.includes('memory') ||
                           this.state.error?.message?.includes('heap');

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">
              {isNetworkError ? '🌐' : isMemoryError ? '💾' : '⚠️'}
            </div>
            
            <h2 className="error-title">
              {isNetworkError ? 'Connection Problem' : 
               isMemoryError ? 'Memory Issue' : 
               'Something went wrong'}
            </h2>
            
            <p className="error-description">
              {isNetworkError ? 
                'Unable to load data. Please check your internet connection and try again.' :
               isMemoryError ?
                'The application is using too much memory. Try refreshing the page.' :
                'We\'re sorry, but something unexpected happened. Our team has been notified.'}
            </p>

            {this.state.retryCount > 0 && (
              <p className="retry-info">
                Retry attempt: {this.state.retryCount}
              </p>
            )}
            
            <div className="error-actions">
              <button 
                className="error-btn primary"
                onClick={this.handleRetry}
                disabled={this.state.retryCount >= 3}
              >
                <i className="fas fa-redo"></i>
                {this.state.retryCount >= 3 ? 'Max retries reached' : 'Try Again'}
              </button>
              
              <button 
                className="error-btn secondary"
                onClick={this.handleReload}
              >
                <i className="fas fa-refresh"></i>
                Reload Page
              </button>
              
              <button 
                className="error-btn tertiary"
                onClick={this.handleGoHome}
              >
                <i className="fas fa-home"></i>
                Go Home
              </button>
            </div>

            {/* Offline support message */}
            {!navigator.onLine && (
              <div className="offline-message">
                <i className="fas fa-wifi-slash"></i>
                You're currently offline. Some features may not work properly.
              </div>
            )}

            {/* Memory usage warning */}
            {!performanceManager.isMemoryOptimal() && (
              <div className="memory-warning">
                <i className="fas fa-exclamation-triangle"></i>
                High memory usage detected. Consider closing other tabs or refreshing the page.
              </div>
            )}
            
            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-info">
                  <h4>Error:</h4>
                  <pre>{this.state.error && this.state.error.toString()}</pre>
                  
                  <h4>Component Stack:</h4>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                  
                  <h4>Error ID:</h4>
                  <code>{this.state.errorId}</code>
                </div>
              </details>
            )}
          </div>

          <style jsx>{`
            .error-boundary {
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 400px;
              padding: 20px;
              background: #f8f9fa;
            }

            .error-boundary-content {
              max-width: 500px;
              text-align: center;
              background: white;
              padding: 40px;
              border-radius: 12px;
              box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            }

            .error-icon {
              font-size: 48px;
              margin-bottom: 20px;
            }

            .error-title {
              color: #d32f2f;
              margin-bottom: 15px;
              font-size: 24px;
              font-weight: 600;
            }

            .error-description {
              color: #666;
              margin-bottom: 20px;
              line-height: 1.5;
            }

            .retry-info {
              color: #ff9800;
              font-size: 14px;
              margin-bottom: 20px;
            }

            .error-actions {
              display: flex;
              flex-direction: column;
              gap: 10px;
              margin-bottom: 20px;
            }

            .error-btn {
              padding: 12px 24px;
              border: none;
              border-radius: 6px;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
            }

            .error-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }

            .error-btn.primary {
              background: #4CAF50;
              color: white;
            }

            .error-btn.primary:hover:not(:disabled) {
              background: #45a049;
            }

            .error-btn.secondary {
              background: #2196F3;
              color: white;
            }

            .error-btn.secondary:hover {
              background: #1976D2;
            }

            .error-btn.tertiary {
              background: #f5f5f5;
              color: #333;
              border: 1px solid #ddd;
            }

            .error-btn.tertiary:hover {
              background: #e0e0e0;
            }

            .offline-message,
            .memory-warning {
              background: #fff3cd;
              color: #856404;
              padding: 10px;
              border-radius: 6px;
              margin-bottom: 15px;
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
            }

            .memory-warning {
              background: #f8d7da;
              color: #721c24;
            }

            .error-details {
              margin-top: 20px;
              text-align: left;
              background: #f8f9fa;
              padding: 15px;
              border-radius: 6px;
            }

            .error-details summary {
              cursor: pointer;
              font-weight: 500;
              margin-bottom: 10px;
            }

            .error-info h4 {
              margin: 15px 0 5px 0;
              color: #333;
            }

            .error-info pre {
              background: #f1f1f1;
              padding: 10px;
              border-radius: 4px;
              overflow-x: auto;
              font-size: 12px;
              white-space: pre-wrap;
            }

            .error-info code {
              background: #f1f1f1;
              padding: 2px 6px;
              border-radius: 3px;
              font-family: monospace;
            }

            /* Mobile responsive */
            @media (max-width: 768px) {
              .error-boundary {
                padding: 15px;
                min-height: 300px;
              }

              .error-boundary-content {
                padding: 30px 20px;
              }

              .error-title {
                font-size: 20px;
              }

              .error-actions {
                flex-direction: column;
              }

              .error-btn {
                padding: 10px 20px;
                font-size: 14px;
              }
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;